import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink2';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../visa';
import { TeamRoleModel } from '../../../../../../../models/team-permissions.model';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { TeamPermStates } from '../../../../../../../modules/permission/team-permissions-controller/states';
import { TeamPermSelectors } from '../../../../../../../modules/permission/team-permissions-controller/selectors';
import { TeamPermActions } from '../../../../../../../modules/permission/team-permissions-controller/actions';
import { PermissionStates } from '../../../../../permission-controller/root';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-team-role-edit',
  templateUrl: './team-role-edit.component.html',
  styleUrls: ['./team-role-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class TeamRoleEditComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 4;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // info. about previous local states
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  toggleColumn$: Observable<boolean>;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  form: FormGroup;

  // selected team role
  teamRole: TeamRoleModel;

  // fields that require options
  status: string;
  statusArr: any = ['ACTIVE', 'INACTIVE'];
  newStatusArr: any = [];

  constructor(
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
   ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      name: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      code: [''],
      currStatus: [''],
      status: ['']
    })

    this.newStatusArr = this.statusArr;

    this.getRole();
  }

  getRole() {
    this.subSink.sink = this.store.select(TeamPermSelectors.selectTeamRole)
      .subscribe((response) => {
        this.teamRole = response;
        this.form.patchValue({
          name: response.app_mst_role.name,
          code: response.app_mst_role.code,
          status: response.app_mst_role.status
        })
      })
  }

  /**
   * This function is called whe the user clicks on the save button.
   * This will dispatch an NgRx action to update details.
   *
   */
   onSubmit() {
     this.store.dispatch(TeamPermActions.updateRoleInit({guid: this.teamRole.app_mst_role.guid.toString(), role: this.form}));
  }

  /**
   * This function is called whe the user clicks on the delete button.
   * This will dispatch an NgRx action to delete the entity.
   *
   */
   onDelete() {
     this.store.dispatch(TeamPermActions.deleteRoleInit({guid: this.teamRole.app_mst_role.guid.toString()}));
     this.onReturn();
  }

  /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyStatusFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newStatusArr = this.statusArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
